/*
Copyright 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type TrackReferenceOrPlaceholder } from "@livekit/components-core";
import { animated } from "@react-spring/web";
import {
  type FC,
  type ComponentProps,
  type CSSProperties,
  type ReactNode,
  type SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { VideoTrack } from "@livekit/components-react";
import { Text, Tooltip } from "@vector-im/compound-web";
import { ErrorSolidIcon } from "@vector-im/compound-design-tokens/assets/web/icons";

import styles from "./MediaView.module.css";
import { Avatar } from "../Avatar";
import { RaisedHandIndicator } from "../reactions/RaisedHandIndicator";
import {
  showConnectionStats as showConnectionStatsSetting,
  showHandRaisedTimer,
  useSetting,
} from "../settings/settings";
import { type ReactionOption } from "../reactions";
import { ReactionIndicator } from "../reactions/ReactionIndicator";
import { RTCConnectionStats } from "../RTCConnectionStats";
import videoPlaceholder from "../graphics/video-placeholder.gif";
import { autoVideoFit } from "../utils/videoFit";

interface Props extends ComponentProps<typeof animated.div> {
  className?: string;
  style?: ComponentProps<typeof animated.div>["style"];
  targetWidth: number;
  targetHeight: number;
  video: TrackReferenceOrPlaceholder | undefined;
  /**
   * How to fit the video content inside the tile. When undefined, MediaView
   * chooses a smart default based on the aspect ratios of the tile and video.
   */
  videoFit?: "cover" | "contain";
  mirror: boolean;
  soundWaves?: boolean;
  userId: string;
  videoEnabled: boolean;
  unencryptedWarning: boolean;
  status?: ReactNode;
  showNameTags: boolean;
  nameTagLeadingIcon?: ReactNode;
  displayName: string;
  mxcAvatarUrl: string | undefined;
  avatarStyle?: "solid" | "translucent";
  background?: "solid" | "transparent";
  focusable: boolean;
  primaryButton?: ReactNode;
  raisedHandTime?: Date;
  currentReaction?: ReactionOption;
  raisedHandOnClick?: () => void;
  waitingForMedia?: boolean;
  audioStreamStats?: RTCInboundRtpStreamStats | RTCOutboundRtpStreamStats;
  videoStreamStats?: RTCInboundRtpStreamStats | RTCOutboundRtpStreamStats;
  rtcBackendIdentity?: string;
  /**
   * The focus url, mainly for debugging purposes.
   */
  focusUrl?: string;
  /**
   * Called whenever the aspect ratio of the video content becomes known or
   * otherwise changes.
   */
  setVideoAspectRatio?: (ratio: number) => void;
}

export const MediaView: FC<Props> = ({
  ref,
  className,
  style,
  targetWidth,
  targetHeight,
  video,
  videoFit,
  mirror,
  soundWaves,
  userId,
  videoEnabled,
  unencryptedWarning,
  showNameTags,
  nameTagLeadingIcon,
  displayName,
  mxcAvatarUrl,
  avatarStyle = "solid",
  background = "solid",
  focusable,
  primaryButton,
  status,
  raisedHandTime,
  currentReaction,
  raisedHandOnClick,
  waitingForMedia,
  audioStreamStats,
  videoStreamStats,
  rtcBackendIdentity,
  focusUrl,
  setVideoAspectRatio: setTheirVideoAspectRatio,
  ...props
}) => {
  const { t } = useTranslation();
  const [handRaiseTimerVisible] = useSetting(showHandRaisedTimer);
  const [showConnectionStats] = useSetting(showConnectionStatsSetting);

  const avatarSize = Math.round(
    Math.min(targetWidth, targetHeight) *
      (soundWaves === undefined ? 0.5 : 0.38),
  );

  const [videoAspectRatio, setOurVideoAspectRatio] = useState<number>(NaN);
  const tileAspectRatio = targetWidth / targetHeight;

  // --- Zoom/pan on the video content (scroll to zoom, drag to pan,
  // double-click or the badge to reset; two-finger pinch on touch) ---
  const zoomNodeRef = useRef<HTMLDivElement | null>(null);
  const zoom = useRef({ scale: 1, x: 0, y: 0 });
  const [zoomed, setZoomed] = useState(false);
  const [zoomStyle, setZoomStyle] = useState<CSSProperties | undefined>(
    undefined,
  );
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinchBase = useRef<{ dist: number; scale: number } | null>(null);

  const clampPan = (
    scale: number,
    x: number,
    y: number,
    rect: { width: number; height: number },
  ) => {
    // Layout zoom grows the video from the container's top-left, so the full
    // pan range needed to reach every corner is [-(scale-1)*size, 0].
    const maxX = rect.width * (scale - 1);
    const maxY = rect.height * (scale - 1);
    return {
      scale,
      x: Math.max(-maxX, Math.min(0, x)),
      y: Math.max(-maxY, Math.min(0, y)),
    };
  };

  const applyZoom = (next: { scale: number; x: number; y: number }) => {
    zoom.current = next;
    const isZoomed = next.scale > 1.01;
    setZoomed(isZoomed);
    // Layout zoom (smartchatty patch): grow the element's real size instead of
    // transform-scaling it, so the browser rasterises the video at full source
    // resolution instead of upscaling an already-rasterised layer.
    setZoomStyle(
      isZoomed
        ? {
            width: `${next.scale * 100}%`,
            height: `${next.scale * 100}%`,
            transform: `translate(${next.x}px, ${next.y}px)`,
          }
        : undefined,
    );
  };

  const zoomAtPoint = (
    scale: number,
    cx: number,
    cy: number,
    rect: { width: number; height: number },
  ) => {
    const cur = zoom.current;
    // The video's top-left sits at (cur.x, cur.y); keep the content point
    // under the cursor fixed while rescaling (top-left anchored zoom).
    const x = cx - (cx - cur.x) * (scale / cur.scale);
    const y = cy - (cy - cur.y) * (scale / cur.scale);
    applyZoom(clampPan(scale, x, y, rect));
  };

  useEffect(() => {
    const el = zoomNodeRef.current;
    if (el === null) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      zoomAtPoint(
        Math.min(
          12,
          Math.max(1, zoom.current.scale * Math.exp(-e.deltaY * 0.0015)),
        ),
        e.clientX - rect.left - rect.width / 2,
        e.clientY - rect.top - rect.height / 2,
        rect,
      );
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Propagate video dimensions
  const setVideoAspectRatio = (ratio: number) => {
    setOurVideoAspectRatio(ratio);
    setTheirVideoAspectRatio?.(ratio);
  };
  const videoRef = (el: HTMLVideoElement | null) => {
    if (el !== null) setVideoAspectRatio(el.videoWidth / el.videoHeight);
  };
  const onResize = (ev: SyntheticEvent<HTMLVideoElement>) =>
    setVideoAspectRatio(
      ev.currentTarget.videoWidth / ev.currentTarget.videoHeight,
    );

  const warnings = unencryptedWarning && (
    <Tooltip
      label={t("common.unencrypted")}
      placement="bottom"
      isTriggerInteractive={false}
      nonInteractiveTriggerTabIndex={focusable ? undefined : -1}
    >
      <ErrorSolidIcon
        width={20}
        height={20}
        className={styles.errorIcon}
        role="img"
        aria-label={t("common.unencrypted")}
      />
    </Tooltip>
  );

  const mergedRef = (node: HTMLDivElement | null) => {
    zoomNodeRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) (ref as { current: HTMLDivElement | null }).current = node;
  };

  const panBy = (dx: number, dy: number) => {
    const el = zoomNodeRef.current;
    if (el === null) return;
    const rect = el.getBoundingClientRect();
    const cur = zoom.current;
    applyZoom(clampPan(cur.scale, cur.x + dx, cur.y + dy, rect));
  };

  return (
    <animated.div
      className={classNames(styles.media, className, {
        [styles.mirror]: mirror,
        [styles.zoomed]: zoomed,
      })}
      style={style}
      ref={mergedRef}
      onPointerDown={(e) => {
        // Track every pointer so a pinch can start from scale 1 (touch
        // devices have no wheel to enter zoom with); panning below still
        // only engages once zoomed, so single-finger touches behave as usual
        e.currentTarget.setPointerCapture(e.pointerId);
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      }}
      onPointerMove={(e) => {
        if (!pointers.current.has(e.pointerId)) return;
        const prev = pointers.current.get(e.pointerId)!;
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointers.current.size >= 2) {
          const [a, b] = [...pointers.current.values()];
          const dist = Math.hypot(a.x - b.x, a.y - b.y);
          const el = zoomNodeRef.current;
          if (el === null) return;
          const rect = el.getBoundingClientRect();
          if (pinchBase.current === null) {
            pinchBase.current = { dist, scale: zoom.current.scale };
          } else {
            zoomAtPoint(
              Math.min(
                12,
                Math.max(
                  1,
                  (pinchBase.current.scale * dist) / pinchBase.current.dist,
                ),
              ),
              (a.x + b.x) / 2 - rect.left - rect.width / 2,
              (a.y + b.y) / 2 - rect.top - rect.height / 2,
              rect,
            );
          }
          return;
        }
        if (zoom.current.scale <= 1.01) return;
        panBy(e.clientX - prev.x, e.clientY - prev.y);
      }}
      onPointerUp={(e) => {
        pointers.current.delete(e.pointerId);
        pinchBase.current = null;
      }}
      onPointerCancel={(e) => {
        pointers.current.delete(e.pointerId);
        pinchBase.current = null;
      }}
      onDoubleClick={() => {
        if (zoom.current.scale > 1.01) applyZoom({ scale: 1, x: 0, y: 0 });
      }}
      data-testid="videoTile"
      data-video-enabled={video && videoEnabled}
      data-video-fit={
        videoFit ?? autoVideoFit(videoAspectRatio, tileAspectRatio)
      }
      data-background={background}
      {...props}
    >
      <div className={styles.bg}>
        {soundWaves !== undefined && (
          <div className={styles.waves} data-visible={soundWaves}>
            <div className={styles.wave} />
            <div className={styles.wave} />
            <div className={styles.wave} />
            <div className={styles.speakingBorder} />
          </div>
        )}
        <Avatar
          id={userId}
          name={displayName}
          size={avatarSize}
          src={mxcAvatarUrl}
          data-style={avatarStyle}
          className={styles.avatar}
          style={{ display: video && videoEnabled ? "none" : "initial" }}
        />
        {video?.publication !== undefined && (
          <VideoTrack
            trackRef={video}
            // There's no reason for this to be focusable
            tabIndex={-1}
            disablePictureInPicture
            data-testid="video"
            // Set the placeholder to a small transparent image. (On Android web
            // views the default poster image is particularly ugly.)
            poster={videoPlaceholder}
            ref={videoRef}
            onResize={onResize}
            style={zoomStyle}
          />
        )}
      </div>
      <div className={styles.fg}>
        {zoomed && (
          <button
            className={styles.zoomBadge}
            onClick={() => applyZoom({ scale: 1, x: 0, y: 0 })}
          >
            {Math.round(zoom.current.scale * 100)}%
          </button>
        )}
        <div className={styles.reactions}>
          <RaisedHandIndicator
            raisedHandTime={raisedHandTime}
            miniature={avatarSize < 96}
            showTimer={handRaiseTimerVisible}
            onClick={raisedHandOnClick}
            tabIndex={focusable ? undefined : -1}
          />
          {currentReaction && (
            <ReactionIndicator
              miniature={avatarSize < 96}
              emoji={currentReaction.emoji}
            />
          )}
        </div>
        {waitingForMedia && (
          <div className={styles.status}>
            {t("video_tile.waiting_for_media")}
            {showConnectionStats ? " " + rtcBackendIdentity : ""}
          </div>
        )}
        {showConnectionStats && (
          <>
            <RTCConnectionStats
              audio={audioStreamStats}
              video={videoStreamStats}
              focusUrl={focusUrl}
              rtcBackendIdentity={rtcBackendIdentity}
            />
          </>
        )}
        {status && <div className={styles.status}>{status}</div>}
        {/* TODO: Bring this back once encryption status is less broken */}
        {/*encryptionStatus !== EncryptionStatus.Okay && (
            <div className={styles.status}>
              <Text as="span" size="sm" weight="medium" className={styles.name}>
                {encryptionStatus === EncryptionStatus.Connecting &&
                  t("e2ee_encryption_status.connecting")}
                {encryptionStatus === EncryptionStatus.KeyMissing &&
                  t("e2ee_encryption_status.key_missing")}
                {encryptionStatus === EncryptionStatus.KeyInvalid &&
                  t("e2ee_encryption_status.key_invalid")}
                {encryptionStatus === EncryptionStatus.PasswordInvalid &&
                  t("e2ee_encryption_status.password_invalid")}
              </Text>
            </div>
          )*/}
        {showNameTags && targetWidth >= 100 ? (
          <div className={styles.nameTag}>
            {nameTagLeadingIcon}
            <Text
              as="span"
              size="sm"
              weight="medium"
              className={styles.name}
              data-testid="name_tag"
            >
              {displayName}
            </Text>
            {warnings}
          </div>
        ) : (
          warnings
        )}
        {primaryButton}
      </div>
    </animated.div>
  );
};

MediaView.displayName = "MediaView";
